import { Button, Card, Empty, Modal, Table, TableColumnType } from 'antd';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { TableAndExcelColumn } from '../../utilites/exportToExcel';
import { BaseCompanyProduct, Company } from '../../models/company-interface';
import { useHistory } from 'react-router';
import CustomFilterDropdown from '../../components/CustomFilterDropdown';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import ListHeader from '../../components/ListHeader';

type Filter = {
  text: string;
  value: number | string;
};

const boolFilters: Filter[] = [
  {
    value: 'Yes',
    text: 'Yes',
  },
  {
    value: 'No',
    text: 'No',
  },
];

export default function DefaultUserPage() {
  const history = useHistory();

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const useGetDefaultList = CustomerServiceQueries.useGetDefaultUserCompanyList();
  const mutateDeleteDefaultList = CustomerServiceQueries.useDeleteDefaultUserFromCompanies();
  
  const yesOrNo = (data: boolean) => !!data ? 'Yes' : 'No'

  const columns: TableColumnType<Company>[] = useMemo(
    () => [
      {
        title: 'Company ID',
        dataIndex: 'id',
        key: 'id',
        visible: true,
        width: '150px',
        render: (id: number, record: Company) => {
          return id;
        },
        sorter: {
          compare: (a: Company, b: Company) =>
            a.id === b.id ? 0 : a.id > b.id ? -1 : 1,
        },
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        visible: true,
        render: (name: string, record: Company) => {
          return (
            <Button
              type='link'
              href={`/admin/company/${record.id}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/company/${record.id}`);
              }}
            >
              {name}
            </Button>
          );
        },
        sorter: {
          compare: (a: Company, b: Company) => a.name.localeCompare(b.name),
        },
      },
      {
        title: 'Demo Company',
        dataIndex: 'isDemoCompany',
        key: 'isDemoCompany',
        visible: true,
        render: (isDemoCompany: boolean, record: Company) => {
          return yesOrNo(isDemoCompany);
        },
        filters: boolFilters,
        onFilter: (value: string | any, record: Company) => {
          let active = yesOrNo(record.isDemoCompany);
          return active === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='activeFilter'
          />
        ),
        sorter: {
          compare: (a: Company, b: Company) =>
            a.confirmed === b.confirmed ? 0 : a.confirmed ? -1 : 1,
        },
      },
      {
        title: 'Active',
        dataIndex: 'confirmed',
        key: 'confirmed',
        visible: true,
        render: (confirmed: boolean, record: Company) => {
          return yesOrNo(confirmed);
        },
        filters: boolFilters,
        onFilter: (value: string | any, record: Company) => {
          let active = yesOrNo(record.confirmed);
          return active === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='activeFilter'
          />
        ),
        sorter: {
          compare: (a: Company, b: Company) =>
            a.confirmed === b.confirmed ? 0 : a.confirmed ? -1 : 1,
        },
      },
    ],
    [history]
  );

  const onSelectChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  };

  const actions = [
    {
        singleName: "Delete User from Company",
        multiName: "Delete User from Companies",
        multiSelect: true,
        icon: faTrash,
        tag: "RemoveUser",
        forceDisable: selectedRowKeys.length === 0,
        onClick: () => {
          Modal.confirm({
            title: `Remove Default User from ${selectedRowKeys.length > 1 ? 'Companies' : 'Company'}`,
            content: "Are you sure you want to remove the default user from these companies? Please make sure that this is not the only user assigned to them.",
            okText: "Yes, Continue",
            onOk: () => {
              mutateDeleteDefaultList.mutateAsync(selectedRowKeys).then(() => {
                setSelectedRowKeys([]);
              });
            }
          })
        }
    },
];


  return (
    <Card
      title="Default User in Companies"
    >
      <ListHeader 
          actions={actions}
          tag="demoFeatures"
          selectedItems={selectedRowKeys?.length ?? 0}
          totalItems={useGetDefaultList.data?.length ?? 0}
          unselectAll={() => { setSelectedRowKeys([]); }}
          ignoreSearch
          reset={true}
      />
      <Table
        columns={columns.filter((x) => x.visible)}
        dataSource={useGetDefaultList?.data}
        loading={useGetDefaultList?.isLoading}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`No companies found`}
            />
          ),
        }}
        rowSelection={rowSelection}
        scroll={{ x: 715 }}
        size='middle'
        rowKey={(val) => val.id}
        pagination={{ defaultPageSize: 20 }}
      />
    </Card>
  )
}